import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Table, Modal, Button, Form, Row, Col, Alert, Tabs, Tab } from 'react-bootstrap';
import {
    FaPlus, FaCheck, FaEdit, FaTrash, FaSortAmountUp, FaSortAmountDown,
    FaChalkboardTeacher, FaArrowLeft, FaArrowRight, FaRedo, FaCog,
    FaCamera, FaTimes, FaCalendarAlt, FaExclamationTriangle, FaImage,
    FaBoxes, FaTools
} from 'react-icons/fa';
import Navbar from '../../dashboard/navbar';
import Sidebar from '../../dashboard/sidebar';
import useAuth from '../../dashboard/useAuth';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const Liste_formation = ({ titre }) => {
    useAuth();
    const [activeTab, setActiveTab] = useState('formations'); // 'formations' ou 'produits'

    // États pour les formations
    const [formation, setFormation] = useState([]);
    const [selectedFormation, setSelectedFormation] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'ascending' });
    const [searchTerm, setSearchTerm] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [alert, setAlert] = useState({ message: '', type: '' });
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingGlobale, setIsLoadingGlobale] = useState(false);

    // États pour les produits
    const [produits, setProduits] = useState([]);
    const [selectedProduit, setSelectedProduit] = useState(null);
    const [searchTermProduit, setSearchTermProduit] = useState('');
    const [showDeleteModalProduit, setShowDeleteModalProduit] = useState(false);
    const [isLoadingProduit, setIsLoadingProduit] = useState(false);
    const [showAjouterModal, setShowAjouterModal] = useState(false);
    const [showModifierModal, setShowModifierModal] = useState(false);
    const [showCodeModal, setShowCodeModal] = useState(false);

    // États pour les formulaires de produits
    const [formDataProduit, setFormDataProduit] = useState({
        nom: '',
        description: '',
        prix: '',
        periode: '',
        photo: null
    });

    // États pour le modal de modification de formation
    const [showEditModal, setShowEditModal] = useState(false);
    const [editFormationData, setEditFormationData] = useState({
        nom: '',
        reference: '',
        prix: '',
        code: '',
        prix_particulier: 0,
        periode: '',
        option: '',
        lieu: '',
        nb_participants: '',
        periode_coaching: '',
    });
    const [selectedDates, setSelectedDates] = useState([]);
    const [photo, setPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState('');
    const [currentPhotoUrl, setCurrentPhotoUrl] = useState('');

    // États pour les codes promo
    const [isLoadingButton, setIsLoadingButton] = useState(false);
    const [promoEndDate, setPromoEndDate] = useState('');
    const [promoPercentage, setPromoPercentage] = useState('');
    const [code_promo, setCode_promo] = useState('');

    // Pagination
    const [currentPageFormation, setCurrentPageFormation] = useState(1);
    const [currentPageProduit, setCurrentPageProduit] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const user_id = localStorage.getItem('user_id');
    const navigate = useNavigate();

    useEffect(() => {
        if (user_id) {
            document.title = "eTrainApp | Gestion Formations & Produits";
        }
    }, [user_id]);

    // ============================================
    // FONCTIONS COMMUNES
    // ============================================

    const showAlert = (message, type = 'success') => {
        setAlert({ message, type });
        setTimeout(() => {
            setAlert({ message: '', type: '' });
        }, 3000);
    };

    // ============================================
    // FONCTIONS POUR LES FORMATIONS
    // ============================================

    const fetchFormation = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(process.env.REACT_APP_HOTE + '/liste-formation/');
            if (response.ok) {
                const data = await response.json();
                setFormation(data);
            } else {
                console.error('Erreur lors de la récupération des formations');
            }
        } catch (error) {
            console.error('Erreur réseau:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Fonctions pour gérer les dates multiples avec validation
    const isValidDate = (dateString) => {
        if (!dateString) return false;
        const date = new Date(dateString);
        return date instanceof Date && !isNaN(date);
    };

    const normalizeDate = (dateString) => {
        if (!dateString) return null;

        // Si c'est déjà au format YYYY-MM-DD
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
            return dateString;
        }

        // Si c'est au format DD/MM/YYYY
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
            const [day, month, year] = dateString.split('/');
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }

        // Essayer de parser avec Date
        const date = new Date(dateString);
        if (isValidDate(date)) {
            return date.toISOString().split('T')[0];
        }

        return null;
    };

    const isDateInPast = (dateString) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const selectedDate = new Date(dateString);
        return selectedDate < today;
    };

    const getMinDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    const handleAddDate = (date) => {
        const dateStr = date;

        if (!isValidDate(dateStr)) {
            showAlert('Date invalide sélectionnée.', 'danger');
            return;
        }

        if (isDateInPast(dateStr)) {
            showAlert('Impossible de sélectionner une date déjà passée.', 'danger');
            return;
        }

        const normalizedDate = normalizeDate(dateStr);
        if (normalizedDate && !selectedDates.includes(normalizedDate)) {
            const updatedDates = [...selectedDates, normalizedDate]
                .map(date => normalizeDate(date))
                .filter(date => date !== null)
                .sort();
            setSelectedDates(updatedDates);
        }
    };

    const handleRemoveDate = (dateToRemove) => {
        const remainingDates = selectedDates.filter(date => date !== dateToRemove);
        const sortedDates = remainingDates
            .map(date => normalizeDate(date))
            .filter(date => date !== null)
            .sort();
        setSelectedDates(sortedDates);
    };

    const formatDateDisplay = (dateString) => {
        const normalizedDate = normalizeDate(dateString);
        if (!normalizedDate) return 'Date invalide';

        const date = new Date(normalizedDate);
        if (!isValidDate(date)) return 'Date invalide';

        return date.toLocaleDateString('fr-FR', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatFormationDate = (formation) => {
        if (isDateList(formation.periode)) {
            try {
                let dates;
                if (typeof formation.periode === 'string') {
                    const cleanedPeriode = formation.periode.replace(/'/g, '"');
                    dates = JSON.parse(cleanedPeriode);
                } else {
                    dates = formation.periode;
                }

                const validDates = dates
                    .map(date => normalizeDate(date))
                    .filter(date => date !== null && isValidDate(new Date(date)))
                    .sort();

                if (validDates.length > 0) {
                    return validDates.map(date => {
                        const d = new Date(date);
                        return isValidDate(d) ? d.toLocaleDateString('fr-FR') : 'Date invalide';
                    }).join(', ');
                }
            } catch (error) {
                console.warn('Erreur de parsing des dates:', error);
            }
        }

        if (formation.date) {
            const normalizedDate = normalizeDate(formation.date);
            if (normalizedDate) {
                const d = new Date(normalizedDate);
                return isValidDate(d) ? d.toLocaleDateString('fr-FR') : 'Date invalide';
            }
        }

        return 'Aucune date';
    };

    const isDateList = (periode) => {
        if (!periode) return false;

        if (typeof periode === 'string') {
            try {
                const cleanedPeriode = periode.replace(/'/g, '"');
                const parsed = JSON.parse(cleanedPeriode);
                return Array.isArray(parsed) && parsed.length > 0;
            } catch (error) {
                return false;
            }
        }

        return Array.isArray(periode) && periode.length > 0;
    };

    const handleOpenEditModal = (formation) => {
        if (formation) {
            setSelectedFormation(formation);
            setEditFormationData({
                nom: formation.nom || '',
                reference: formation.reference || '',
                prix: formation.prix || 0,
                prix_particulier: formation.prix_particulier || 0,
                periode: formation.periode || '',
                option: formation.option || '',
                code: formation.code || '',
                lieu: formation.lieu || '',
                nb_participants: formation.nb_participants || 0,
                periode_coaching: formation.periode_coaching || '',
            });

            let datesArray = [];

            if (formation.periode && typeof formation.periode === 'string') {
                try {
                    const cleanedPeriode = formation.periode.replace(/'/g, '"');
                    const parsedPeriode = JSON.parse(cleanedPeriode);
                    if (Array.isArray(parsedPeriode)) {
                        datesArray = parsedPeriode
                            .map(date => normalizeDate(date))
                            .filter(date => date !== null && isValidDate(new Date(date)))
                            .sort();
                    }
                } catch (error) {
                    const normalizedDate = normalizeDate(formation.periode);
                    if (normalizedDate && isValidDate(new Date(normalizedDate))) {
                        datesArray = [normalizedDate];
                    }
                }
            }

            if (datesArray.length === 0 && formation.date_debut) {
                const normalizedDate = normalizeDate(formation.date_debut);
                if (normalizedDate && isValidDate(new Date(normalizedDate))) {
                    datesArray = [normalizedDate];
                }
            }

            setSelectedDates(datesArray);

            if (formation.photo) {
                setCurrentPhotoUrl(process.env.REACT_APP_HOTE + formation.photo);
                setPhotoPreview(process.env.REACT_APP_HOTE + formation.photo);
            } else {
                setCurrentPhotoUrl('');
                setPhotoPreview('');
            }

            setPhoto(null);
            setShowEditModal(true);
        }
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setEditFormationData({
            nom: '',
            reference: '',
            prix: '',
            periode: '',
            option: '',
            code: '',
            lieu: '',
            nb_participants: '',
            periode_coaching: '',
        });
        setSelectedDates([]);
        setPhoto(null);
        setPhotoPreview('');
    };

    const handlePhotoChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                showAlert('La taille de l\'image ne doit pas dépasser 5MB', 'danger');
                return;
            }

            if (!file.type.startsWith('image/')) {
                showAlert('Veuillez sélectionner une image valide', 'danger');
                return;
            }

            setPhoto(file);

            const reader = new FileReader();
            reader.onload = (e) => {
                setPhotoPreview(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemovePhoto = () => {
        setPhoto(null);
        setPhotoPreview(currentPhotoUrl);
        document.getElementById('edit-photo-upload').value = '';
    };

    const handleEditSubmit = async (event) => {
        event.preventDefault();

        try {
            const formData = new FormData();
            formData.append('nom', editFormationData.nom);
            formData.append('reference', editFormationData.reference);
            formData.append('prix', editFormationData.prix);
            formData.append('code', editFormationData.code);
            formData.append('prix_particulier', editFormationData.prix_particulier);
            formData.append('periode', editFormationData.periode);
            formData.append('option', editFormationData.option);
            formData.append('lieu', editFormationData.lieu);
            formData.append('nb_participants', editFormationData.nb_participants);
            formData.append('periode_coaching', editFormationData.periode_coaching);
            formData.append('dates_formation', JSON.stringify(selectedDates.sort()));

            if (photo) {
                formData.append('photo', photo);
            }

            const response = await fetch(process.env.REACT_APP_HOTE + `/modifier-formation/${selectedFormation.id}/`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                showAlert('Une erreur est survenue: ' + errorData.error, 'danger');
                handleCloseEditModal();
            } else {
                showAlert('Formation modifiée avec succès', 'success');
                fetchFormation();
                handleCloseEditModal();
                setSelectedFormation(null)
            }
        } catch (error) {
            console.error('Erreur réseau:', error);
            showAlert('Erreur réseau : ' + error.message, 'danger');
        }
    };

    const handleActiver = async (formation) => {
        try {
            const response = await fetch(process.env.REACT_APP_HOTE + `/activer-desactiver-formation/${formation.id}/`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                showAlert('Une erreur est survenue: ' + errorData.error, 'danger');
            } else {
                showAlert('Statut changé avec succès', 'success');
                fetchFormation();
            }
        } catch (error) {
            console.error('Erreur réseau:', error);
            showAlert('Erreur réseau : ' + error.message, 'danger');
        }
    };

    const handleDeleteFormation = async () => {
        if (selectedFormation) {
            try {
                const response = await fetch(process.env.REACT_APP_HOTE + `/supprimer-formation/${selectedFormation.id}/`, {
                    method: 'DELETE',
                });

                if (response.ok) {
                    setFormation(formation.filter((formation) => formation.id !== selectedFormation.id));
                    setSelectedFormation(null);
                    showAlert("Formation supprimée avec succès !", 'success');
                } else {
                    console.error('Erreur lors de la suppression de la formation');
                    showAlert("Erreur lors de la suppression", 'danger');
                }
            } catch (error) {
                console.error('Erreur réseau:', error);
                showAlert("Erreur réseau", 'danger');
            } finally {
                setShowDeleteModal(false);
            }
        }
    };



    const [showAllDates, setShowAllDates] = useState(false);
    const [expandedDates, setExpandedDates] = useState(false);

    const getAllDatesDisplay = (periode, formationId) => {
        // Vérifier si periode est vide, null, ou un tableau vide
        if (!periode || periode === '[]' || periode === '""' || periode === "''") {
            return 'A la demande';
        }

        if (isDateList(periode)) {
            try {
                let dates;
                if (typeof periode === 'string') {
                    // Nettoyer la chaîne - remplacer les guillemets simples par des doubles
                    const cleanedPeriode = periode.replace(/'/g, '"');
                    dates = JSON.parse(cleanedPeriode);
                } else {
                    dates = periode;
                }

                // Vérifier si le tableau est vide après parsing
                if (!Array.isArray(dates) || dates.length === 0) {
                    return 'A la demande';
                }

                const validDates = dates
                    .map(date => normalizeDate(date))
                    .filter(date => date !== null && isValidDate(new Date(date)))
                    .sort();

                if (validDates.length > 0) {
                    const isExpanded = expandedDates[formationId];
                    const displayDates = isExpanded ? validDates : validDates.slice(0, 3);
                    const remainingCount = validDates.length - 3;

                    const toggleDates = (e) => {
                        e.stopPropagation(); // Empêcher la sélection de la ligne
                        setExpandedDates(prev => ({
                            ...prev,
                            [formationId]: !prev[formationId]
                        }));
                    };

                    // Déterminer la couleur en fonction de la sélection
                    const isSelected = selectedFormation && selectedFormation.id === formationId;
                    const textColorClass = isSelected ? 'text-light' : 'text-black';
                    const linkColorClass = isSelected ? 'text-white' : 'text-primary';

                    return (
                        <div className="small" style={{
                            wordWrap: 'break-word',
                            whiteSpace: 'normal',
                            lineHeight: '1.4'
                        }}>
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '4px',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                {displayDates.map((date, index) => {
                                    const dateObj = new Date(date);
                                    return (
                                        <span
                                            key={index}
                                            className={textColorClass}
                                            style={{
                                                whiteSpace: 'nowrap',
                                                flexShrink: 0
                                            }}
                                        >
                                            {dateObj.toLocaleDateString('fr-FR', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: '2-digit'
                                            })}
                                            {index < displayDates.length - 1 && ','}
                                        </span>
                                    );
                                })}
                                {!isExpanded && remainingCount > 0 && (
                                    <span
                                        className={`${linkColorClass} ms-1`}
                                        style={{
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                            flexShrink: 0,
                                            whiteSpace: 'nowrap'
                                        }}
                                        onClick={toggleDates}
                                        title="Cliquer pour voir toutes les dates"
                                    >
                                        ...(+{remainingCount})
                                    </span>
                                )}
                                {isExpanded && remainingCount > 0 && (
                                    <span
                                        className={`${linkColorClass} ms-1`}
                                        style={{
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                            flexShrink: 0,
                                            whiteSpace: 'nowrap'
                                        }}
                                        onClick={toggleDates}
                                        title="Cliquer pour réduire"
                                    >
                                        (réduire)
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                } else {
                    return 'A la demande';
                }
            } catch (error) {
                console.warn('Erreur de parsing des dates:', error);
                return 'A la demande';
            }
        }

        // Si ce n'est pas une liste de dates mais une chaîne non vide
        if (typeof periode === 'string' && periode.trim() !== '' && periode !== '[]') {
            const isSelected = selectedFormation && selectedFormation.id === formationId;
            const textColorClass = isSelected ? 'text-light' : 'text-muted';

            return (
                <span className={`small ${textColorClass}`}>
                    {periode}
                </span>
            );
        }

        return 'A la demande';
    };

    const handleGenererCode = async () => {
        if (!promoEndDate || !promoPercentage) {
            showAlert('Veuillez remplir tous les champs', 'danger');
            return;
        }

        setIsLoadingButton(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_HOTE}/attribuer-code-formation/${selectedFormation.id}/`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    date_fin: promoEndDate,
                    pourcentage: promoPercentage,
                    code_promo: code_promo
                }),
            });
            const data = await response.json();

            if (response.ok) {
                showAlert(data.message, 'success');
                fetchFormation();
                setShowCodeModal(false);
                setPromoEndDate('');
                setPromoPercentage('');
                setCode_promo('');
            } else {
                showAlert(data.error, 'danger');
            }
        } catch (error) {
            console.error('Erreur lors de la génération du code:', error);
            showAlert(error.message, 'danger');
        } finally {
            setIsLoadingButton(false);
        }
    };

    // ============================================
    // FONCTIONS POUR LES PRODUITS
    // ============================================

    const fetchProduits = async () => {
        setIsLoadingProduit(true);
        try {
            const response = await fetch(process.env.REACT_APP_HOTE + '/liste-produit/');
            if (response.ok) {
                const data = await response.json();
                setProduits(data);
            } else {
                console.error('Erreur lors de la récupération des produits');
            }
        } catch (error) {
            console.error('Erreur réseau:', error);
        } finally {
            setIsLoadingProduit(false);
        }
    };

    const handleRowClickProduit = (produit) => {
        setSelectedProduit(produit);
    };

    const handleDeleteProduit = async () => {
        if (selectedProduit) {
            try {
                const response = await fetch(process.env.REACT_APP_HOTE + `/supprimer-produit/${selectedProduit.id}/`, {
                    method: 'DELETE',
                });

                if (response.ok) {
                    setProduits(produits.filter((produit) => produit.id !== selectedProduit.id));
                    showAlert('Produit supprimé avec succès !');
                } else {
                    console.error('Erreur lors de la suppression du produit');
                }
            } catch (error) {
                console.error('Erreur réseau:', error);
            } finally {
                setShowDeleteModalProduit(false);
                setSelectedProduit(null);
            }
        }
    };

    const handleOpenAjouterModal = () => {
        setFormDataProduit({
            nom: '',
            description: '',
            prix: '',
            periode: '',
            photo: null
        });
        setShowAjouterModal(true);
    };

    const handleCloseAjouterModal = () => {
        setShowAjouterModal(false);
        setFormDataProduit({
            nom: '',
            description: '',
            prix: '',
            periode: '',
            photo: null
        });
    };

    const handleOpenModifierModal = () => {
        if (selectedProduit) {
            setFormDataProduit({
                nom: selectedProduit.nom,
                description: selectedProduit.description,
                prix: selectedProduit.prix,
                periode: selectedProduit.periode,
                photo: null
            });
            setShowModifierModal(true);
        }
    };

    const handleCloseModifierModal = () => {
        setShowModifierModal(false);
        setFormDataProduit({
            nom: '',
            description: '',
            prix: '',
            periode: '',
            photo: null
        });
    };

    const handleInputChangeProduit = (e) => {
        const { name, value } = e.target;
        setFormDataProduit(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDescriptionChangeProduit = (value) => {
        setFormDataProduit(prev => ({
            ...prev,
            description: value
        }));
    };

    const handleFileChangeProduit = (e) => {
        setFormDataProduit(prev => ({
            ...prev,
            photo: e.target.files[0]
        }));
    };

    const handleAjouterProduit = async (e) => {
        e.preventDefault();

        const submitFormData = new FormData();
        submitFormData.append('nom', formDataProduit.nom);
        submitFormData.append('description', formDataProduit.description);
        submitFormData.append('prix', formDataProduit.prix);
        submitFormData.append('periode', formDataProduit.periode);
        if (formDataProduit.photo) {
            submitFormData.append('photo', formDataProduit.photo);
        }

        try {
            const response = await fetch(process.env.REACT_APP_HOTE + `/ajouter-produit/${user_id}/`, {
                method: 'POST',
                body: submitFormData,
            });

            if (response.ok) {
                showAlert('Produit ajouté avec succès !');
                handleCloseAjouterModal();
                fetchProduits();
            } else {
                const errorData = await response.json();
                showAlert(errorData.error || 'Erreur lors de l\'ajout du produit', 'danger');
            }
        } catch (error) {
            console.error('Erreur réseau:', error);
            showAlert('Erreur réseau lors de l\'ajout du produit', 'danger');
        }
    };

    const handleModifierProduit = async (e) => {
        e.preventDefault();

        const submitFormData = new FormData();
        submitFormData.append('nom', formDataProduit.nom);
        submitFormData.append('description', formDataProduit.description);
        submitFormData.append('prix', formDataProduit.prix);
        submitFormData.append('periode', formDataProduit.periode);
        if (formDataProduit.photo) {
            submitFormData.append('photo', formDataProduit.photo);
        }

        try {
            const response = await fetch(process.env.REACT_APP_HOTE + `/modifier-produit/${selectedProduit.id}/`, {
                method: 'POST',
                body: submitFormData,
            });

            if (response.ok) {
                showAlert('Produit modifié avec succès !');
                handleCloseModifierModal();
                fetchProduits();
            } else {
                const errorData = await response.json();
                showAlert(errorData.error || 'Erreur lors de la modification du produit', 'danger');
            }
        } catch (error) {
            console.error('Erreur réseau:', error);
            showAlert('Erreur réseau lors de la modification du produit', 'danger');
        }
    };

    // ============================================
    // FONCTIONS DE PAGINATION ET TRI
    // ============================================

    const filteredFormation = formation.filter(f =>
        f.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.statut?.toString().includes(searchTerm.toLowerCase()) ||
        f.lieu?.toString().includes(searchTerm.toLowerCase()) ||
        f.nb_participants?.toString().includes(searchTerm.toLowerCase()) ||
        f.ajoute_par?.toString().includes(searchTerm.toLowerCase())
    );

    const filteredProduits = produits.filter(produit =>
        produit.nom.toLowerCase().includes(searchTermProduit.toLowerCase()) ||
        produit.description?.toLowerCase().includes(searchTermProduit.toLowerCase()) ||
        produit.ajoute_par?.toLowerCase().includes(searchTermProduit.toLowerCase())
    );

    // Pagination pour formations
    const indexOfLastFormation = currentPageFormation * itemsPerPage;
    const indexOfFirstFormation = indexOfLastFormation - itemsPerPage;
    const currentFormation = filteredFormation.slice(indexOfFirstFormation, indexOfLastFormation);
    const totalPagesFormation = Math.ceil(filteredFormation.length / itemsPerPage);
    const totalItemsFormation = filteredFormation.length;

    // Pagination pour produits
    const indexOfLastProduit = currentPageProduit * itemsPerPage;
    const indexOfFirstProduit = indexOfLastProduit - itemsPerPage;
    const currentProduits = filteredProduits.slice(indexOfFirstProduit, indexOfLastProduit);
    const totalPagesProduit = Math.ceil(filteredProduits.length / itemsPerPage);
    const totalItemsProduit = filteredProduits.length;

    const handleSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        if (activeTab === 'formations') {
            setCurrentPageFormation(1);
        } else {
            setCurrentPageProduit(1);
        }
    };

    // ============================================
    // FONCTIONS POUR LES BOUTONS D'ACTION
    // ============================================

    const renderActionButtons = () => {
        if (activeTab === 'formations') {
            return (
                <div className='row'>
                    <Link to={`/Ajouter-formation`} className="btn btn-success d-flex align-items-center justify-content-center ms-2 mr-2 mt-1">
                        <FaPlus />
                        <span className="d-none d-sm-inline ml-2">
                            Ajouter formation
                        </span>
                    </Link>
                    <button
                        onClick={() => handleOpenEditModal(selectedFormation)}
                        className={`btn d-flex align-items-center justify-content-center ms-2 mr-2 mt-1 ${selectedFormation ? 'btn-success' : 'btn-default text-dark'}`}
                        disabled={!selectedFormation}
                    >
                        <FaEdit />
                        <span className="d-none d-sm-inline ml-2">
                            Modifier
                        </span>
                    </button>
                    <button
                        onClick={() => handleActiver(selectedFormation)}
                        className={`btn d-flex align-items-center justify-content-center ms-2 mr-2 mt-1 ${selectedFormation ? 'btn-success' : 'btn-default text-dark'}`}
                        disabled={!selectedFormation}
                    >
                        <FaCheck />
                        <span className="d-none d-sm-inline ml-2">
                            Activer/Désactiver
                        </span>
                    </button>
                    {isLoadingButton ? (
                        <button className="btn btn-success mr-2" disabled>
                            <i className="fa fa-spinner fa-spin mr-1"></i>
                            <span className="d-none d-sm-inline ml-2">
                                Chargement...
                            </span>
                        </button>
                    ) : (
                        <button
                            onClick={() => setShowCodeModal(true)}
                            className={`btn d-flex align-items-center justify-content-center ms-2 mr-2 mt-1 ${selectedFormation ? 'btn-success' : 'btn-default text-dark'}`}
                            disabled={!selectedFormation}
                        >
                            {selectedFormation?.code_formation ? (<FaRedo />) : (<FaCog />)}
                            <span className="d-none d-sm-inline ml-1">
                                {selectedFormation?.code_formation ? 'Réinitialiser' : 'Générer code'}
                            </span>
                        </button>
                    )}
                    <button
                        onClick={() => setShowDeleteModal(true)}
                        className={`btn d-flex align-items-center justify-content-center ms-2 mr-2 mt-1 ${selectedFormation ? 'btn-success' : 'btn-default text-dark'}`}
                        disabled={!selectedFormation}
                    >
                        <FaTrash />
                        <span className="d-none d-sm-inline ml-2">
                            Supprimer
                        </span>
                    </button>
                </div>
            );
        } else if (activeTab === 'produits') {
            return (
                <div className='row'>
                    <button
                        onClick={handleOpenAjouterModal}
                        className="btn btn-success d-flex align-items-center justify-content-center ms-2 mr-2 mt-1"
                    >
                        <FaPlus />
                        <span className="d-none d-sm-inline ml-2">
                            Ajouter produit
                        </span>
                    </button>
                    <button
                        onClick={handleOpenModifierModal}
                        className={`btn d-flex align-items-center justify-content-center ms-2 mr-2 mt-1 ${selectedProduit ? 'btn-success' : 'btn-default text-dark'}`}
                        disabled={!selectedProduit}
                    >
                        <FaEdit />
                        <span className="d-none d-sm-inline ml-2">
                            Modifier
                        </span>
                    </button>
                    <button
                        onClick={() => setShowDeleteModalProduit(true)}
                        className={`btn d-flex align-items-center justify-content-center ms-2 mr-2 mt-1 ${selectedProduit ? 'btn-success' : 'btn-default text-dark'}`}
                        disabled={!selectedProduit}
                    >
                        <FaTrash />
                        <span className="d-none d-sm-inline ml-2">
                            Supprimer
                        </span>
                    </button>
                </div>
            );
        }
    };

    // ============================================
    // FONCTIONS POUR LES TABLES
    // ============================================

    const renderTable = () => {
        if (activeTab === 'formations') {
            return (
                <>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="me-2">
                            <select
                                value={itemsPerPage}
                                onChange={handleItemsPerPageChange}
                                className="form-select bg-default p-1"
                                style={{ width: 'auto', border: '1px black solid', borderRadius: '5px' }}
                            >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                            </select>
                        </div>
                        <input
                            type="text"
                            placeholder="Rechercher formation..."
                            className="form-control"
                            style={{ maxWidth: '150px', width: '100%' }}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <table className="footable table table-stripped footable-loaded tablet breakpoint">
                        <thead>
                            <tr>
                                <th className='text-secondary text-wrap d-none d-md-table-cell'>Photo</th>
                                <th className='text-secondary text-wrap d-none d-md-table-cell'>Nom</th>
                                <th className='text-secondary text-wrap d-none d-md-table-cell text-center'>Code formation</th>
                                <th className='text-secondary text-wrap d-none d-md-table-cell text-center'>Tarif public</th>
                                <th className='text-secondary text-wrap d-none d-md-table-cell text-center'>Tarif particulier</th>
                                <th className='text-secondary text-wrap d-none d-md-table-cell text-center'>Periode</th>
                                <th className='text-secondary text-wrap d-none d-md-table-cell text-center'>Lieu</th>
                                {/* <th className='text-secondary text-wrap d-none d-md-table-cell'>Ajouté par</th> */}
                                <th className='text-secondary text-wrap d-none d-md-table-cell text-center'>Statut</th>
                                <th className='text-secondary text-wrap d-none d-md-table-cell text-center'>Code promo</th>
                                <th className='text-secondary text-wrap d-none d-md-table-cell text-center'>Date fin promo</th>
                                <th className='text-secondary text-wrap d-none d-md-table-cell text-center'>Pourcentage promo</th>
                            </tr>
                        </thead>

                        {!isLoading ? (
                            <tbody>
                                {currentFormation.length > 0 ? currentFormation.map((f) => (
                                    <tr
                                        key={f.id}
                                        className={`bg-white ${selectedFormation && selectedFormation.id === f.id ? 'bg-success text-white' : ''}`}
                                        onClick={() => setSelectedFormation(f)}
                                    >
                                        <td>
                                            <img
                                                src={`${process.env.REACT_APP_HOTE}${f.photo_url}`}
                                                alt={f.nom}
                                                style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    objectFit: 'cover',
                                                    borderRadius: '5px',
                                                }}
                                            />
                                            <div className='lesInfos'>
                                                <small>prix : {f.prix} FCFA HT</small>
                                                <small>Période : {f.periode}</small>
                                                <small>Lieu : {f.lieu}</small>
                                                <small>Date : {formatFormationDate(f)}</small><br />
                                                {/* <small>Ajouté par : {f.ajoute_par}</small> */}
                                                <small>Code promo : {f.code_formation}</small>
                                            </div>
                                        </td>
                                        <td className="d-none d-md-table-cell">
                                            <span
                                                className="d-inline-block text-truncate"
                                                style={{ maxWidth: '100px' }}
                                                title={f.nom}
                                            >
                                                {f.nom}
                                            </span>
                                        </td>
                                        <td className="d-none d-md-table-cell text-center">{f.code}</td>
                                        <td className="d-none d-md-table-cell text-center">
                                            {parseInt(f.prix || 0).toLocaleString('fr-FR')} FCFA HT
                                        </td>
                                        <td className="d-none d-md-table-cell text-center">
                                            {parseInt(f.prix_particulier || 0).toLocaleString('fr-FR')} FCFA HT
                                        </td>
                                        <td
                                            className="d-none d-md-table-cell text-center"
                                            style={{
                                                minWidth: '150px',
                                                maxWidth: '300px',
                                                wordWrap: 'break-word'
                                            }}
                                        >
                                            <div className="formation-dates-list">
                                                {getAllDatesDisplay(f.periode, f.id)}
                                            </div>
                                        </td>
                                        <td className="d-none d-md-table-cell text-center">{f.lieu}</td>
                                        {/* <td className="d-none d-md-table-cell">{f.ajoute_par}</td> */}
                                        <td className="d-none d-md-table-cell text-center">{f.statut}</td>
                                        <td className="d-none d-md-table-cell text-center">{f.code_formation}</td>
                                        <td className="d-none d-md-table-cell text-center">
                                            {f?.date_fin_promo ? new Date(f?.date_fin_promo).toLocaleDateString() : ''}
                                        </td>
                                        <td className="d-none d-md-table-cell text-center">
                                            {f.pourcentage_promo ? `${f.pourcentage_promo}%` : ''}
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="10" className="text-center">Aucune formation trouvée</td>
                                    </tr>
                                )}
                            </tbody>
                        ) : (
                            <tbody>
                                <tr>
                                    <td colSpan="8" className="text-center p-5">
                                        <div className="d-flex justify-content-center align-items-center">
                                            <div className="spiner-example">
                                                <div className="sk-spinner sk-spinner-wave">
                                                    <div className="sk-rect1 mr-1"></div>
                                                    <div className="sk-rect2 mr-1"></div>
                                                    <div className="sk-rect3 mr-1"></div>
                                                    <div className="sk-rect4 mr-1"></div>
                                                    <div className="sk-rect5"></div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mt-2">Chargement des produits...</div>
                                    </td>
                                </tr>
                            </tbody>
                        )}
                    </table>
                    {/* Pagination pour formations */}
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="dataTables_info" role="status" aria-live="polite">
                            {totalItemsFormation === 0
                                ? "Aucune formation à afficher"
                                : `Élément ${indexOfFirstFormation + 1} à ${Math.min(indexOfLastFormation, totalItemsFormation)} sur ${totalItemsFormation}`}
                        </div>
                        <div>
                            <button
                                onClick={() => setCurrentPageFormation(currentPageFormation > 1 ? currentPageFormation - 1 : 1)}
                                className="btn btn-light mx-1"
                                disabled={currentPageFormation === 1}
                            >
                                <FaArrowLeft />
                            </button>

                            {(() => {
                                const pagesToShow = 5;
                                let startPage = Math.max(1, currentPageFormation - Math.floor(pagesToShow / 2));
                                let endPage = Math.min(totalPagesFormation, startPage + pagesToShow - 1);

                                if (endPage - startPage < pagesToShow - 1) {
                                    startPage = Math.max(1, endPage - pagesToShow + 1);
                                }

                                const pageButtons = [];

                                for (let i = startPage; i <= endPage; i++) {
                                    pageButtons.push(
                                        <button
                                            key={i}
                                            onClick={() => setCurrentPageFormation(i)}
                                            className={`btn ${currentPageFormation === i ? 'btn-default' : 'btn-light'} mx-1`}
                                        >
                                            {i}
                                        </button>
                                    );
                                }

                                return pageButtons;
                            })()}

                            <button
                                onClick={() => setCurrentPageFormation(currentPageFormation < totalPagesFormation ? currentPageFormation + 1 : totalPagesFormation)}
                                className="btn btn-light mx-1"
                                disabled={currentPageFormation === totalPagesFormation}
                            >
                                <FaArrowRight />
                            </button>

                            <button
                                onClick={() => setCurrentPageFormation(totalPagesFormation)}
                                className="btn btn-light mx-1"
                                disabled={currentPageFormation === totalPagesFormation}
                            >
                                {totalPagesFormation}
                            </button>
                        </div>
                    </div>
                </>
            );
        } else if (activeTab === 'produits') {
            return (
                <>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="me-2">
                            <select
                                value={itemsPerPage}
                                onChange={handleItemsPerPageChange}
                                className="form-select bg-default p-1"
                                style={{ width: 'auto', border: '1px black solid', borderRadius: '5px' }}
                            >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                            </select>
                        </div>
                        <input
                            type="text"
                            placeholder="Rechercher produit..."
                            className="form-control"
                            style={{ maxWidth: '150px', width: '100%' }}
                            value={searchTermProduit}
                            onChange={(e) => setSearchTermProduit(e.target.value)}
                        />
                    </div>
                    <div className="table-responsive" style={{ fontSize: '11px' }}>
                        <table className="footable table table-stripped footable-loaded tablet breakpoint">
                            <thead>
                                <tr>
                                    <th className="text-secondary text-wrap d-none d-md-table-cell">Image</th>
                                    <th className='text-secondary text-wrap d-none d-md-table-cell'>Nom</th>
                                    <th className='text-secondary text-wrap d-none d-md-table-cell'>Description</th>
                                    <th className='text-secondary text-wrap d-none d-md-table-cell'>Prix</th>
                                    <th className='text-secondary text-wrap d-none d-md-table-cell'>Ajouté par</th>
                                    <th className='text-secondary text-wrap d-none d-md-table-cell'>Période</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentProduits.map((produit) => (
                                    <tr
                                        key={produit.id}
                                        onClick={() => handleRowClickProduit(produit)}
                                        className={`bg-white ${selectedProduit && selectedProduit.id === produit.id ? 'bg-success text-white' : ''}`}
                                    >
                                        <td>
                                            <img
                                                src={`${process.env.REACT_APP_HOTE}${produit.photo_url}`}
                                                alt={produit.nom}
                                                style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    objectFit: 'cover',
                                                    borderRadius: '50%',
                                                }}
                                            />
                                        </td>
                                        <td>{produit.nom}</td>
                                        <td className="d-none d-md-table-cell">
                                            <div
                                                dangerouslySetInnerHTML={{ __html: produit.description }}
                                                style={{
                                                    maxHeight: '3em',
                                                    overflow: 'hidden',
                                                    whiteSpace: 'pre-wrap',
                                                }}
                                            />
                                        </td>
                                        <td className="d-none d-md-table-cell">
                                            {produit.prix} FCFA
                                        </td>
                                        <td className="d-none d-md-table-cell">
                                            {produit.ajoute_par}
                                        </td>
                                        <td className="d-none d-md-table-cell">
                                            {produit.periode ? `${produit.periode / 730} mois` : "Non spécifié"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {/* Pagination pour produits */}
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="dataTables_info" role="status" aria-live="polite">
                            {totalItemsProduit === 0
                                ? "Aucun produit à afficher"
                                : `Élément ${indexOfFirstProduit + 1} à ${Math.min(indexOfLastProduit, totalItemsProduit)} sur ${totalItemsProduit}`}
                        </div>
                        <div>
                            <button
                                onClick={() => setCurrentPageProduit(currentPageProduit > 1 ? currentPageProduit - 1 : 1)}
                                className="btn btn-light mx-1"
                                disabled={currentPageProduit === 1}
                            >
                                <FaArrowLeft />
                            </button>

                            {(() => {
                                const pagesToShow = 5;
                                let startPage = Math.max(1, currentPageProduit - Math.floor(pagesToShow / 2));
                                let endPage = Math.min(totalPagesProduit, startPage + pagesToShow - 1);

                                if (endPage - startPage < pagesToShow - 1) {
                                    startPage = Math.max(1, endPage - pagesToShow + 1);
                                }

                                const pageButtons = [];

                                for (let i = startPage; i <= endPage; i++) {
                                    pageButtons.push(
                                        <button
                                            key={i}
                                            onClick={() => setCurrentPageProduit(i)}
                                            className={`btn ${currentPageProduit === i ? 'btn-default' : 'btn-light'} mx-1`}
                                        >
                                            {i}
                                        </button>
                                    );
                                }

                                return pageButtons;
                            })()}

                            <button
                                onClick={() => setCurrentPageProduit(currentPageProduit < totalPagesProduit ? currentPageProduit + 1 : totalPagesProduit)}
                                className="btn btn-light mx-1"
                                disabled={currentPageProduit === totalPagesProduit}
                            >
                                <FaArrowRight />
                            </button>

                            <button
                                onClick={() => setCurrentPageProduit(totalPagesProduit)}
                                className="btn btn-light mx-1"
                                disabled={currentPageProduit === totalPagesProduit}
                            >
                                {totalPagesProduit}
                            </button>
                        </div>
                    </div>
                </>
            );
        }
    };

    // ============================================
    // GESTION DES DROITS D'ACCÈS
    // ============================================

    const [accesGroupe, setAccesGroupe] = useState([]);
    const [groupeId, setGroupeId] = useState(null);
    const groupe = localStorage.getItem('groupe');

    const verifGroupe = async () => {
        const groupeStored = localStorage.getItem('groupe');
        const accesGroupeStored = localStorage.getItem('accesGroupe');

        if (groupeStored && accesGroupeStored) {
            setGroupeId(groupeStored);
            setAccesGroupe(JSON.parse(accesGroupeStored));
        } else {
            try {
                const response = await axios.get(`${process.env.REACT_APP_HOTE}/verif-groupe/${user_id}/${groupe}/`);
                if (response.status === 200) {
                    const data = response.data;
                    setGroupeId(data.groupe);

                    let cleanedAccessArray = [];
                    if (Array.isArray(data.acces)) {
                        cleanedAccessArray = data.acces.map((item) =>
                            item.acces
                                ? item.acces.replace(/[\[\]']/g, '').trim().split(',').map((access) => access.trim())
                                : []
                        ).flat();

                        setAccesGroupe(cleanedAccessArray);
                    }

                    localStorage.setItem('groupe', data.groupe);
                    localStorage.setItem('accesGroupe', JSON.stringify(cleanedAccessArray));
                }
            } catch (error) {
                console.error('Erreur lors de la vérification du groupe', error);
            }
        }
    };

    useEffect(() => {
        if (user_id) {
            verifGroupe();
            fetchFormation();
            fetchProduits();
        }
    }, [user_id]);

    if (!accesGroupe.includes("Formation") && !accesGroupe.includes("Produits")) {
        return (
            <div id="wrapper">
                <Sidebar user_id={user_id} titre={titre} />
                <div id="page-wrapper" className="gray-bg">
                    <Navbar />
                    <div className="wrapper wrapper-content animated fadeInRight">
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="ibox">
                                    <div className="ibox-content text-center p-5">
                                        <div className="mb-4">
                                            <i className="fa fa-lock fa-5x text-warning"></i>
                                        </div>
                                        <h2>Accès restreint</h2>
                                        <p className="lead mb-4">
                                            Vous n'avez pas les autorisations nécessaires pour accéder à cette page.
                                        </p>
                                        <div className="d-flex justify-content-center">
                                            <Link to="/" className="btn btn-primary">
                                                <i className="fa fa-arrow-left mr-2"></i>
                                                Retour
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Modules pour ReactQuill
    const modules = {
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline'],
            ['link', 'image'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['clean'],
            [{ 'align': [] }],
        ],
    };

    return (
        <div id="wrapper">
            <Sidebar user_id={user_id} titre={titre} />
            <div id="page-wrapper" className="gray-bg">
                <Navbar />
                <div>
                    <div className="row wrapper border-bottom white-bg page-heading">
                        <div className="col-lg-12">
                            <h2 className='d-flex align-items-center'>
                                {activeTab === 'formations' ?
                                    <><FaChalkboardTeacher size={25} className='mr-2' />Gestion des formations</> :
                                    <><FaBoxes size={25} className='mr-2' />Gestion des produits</>
                                }
                            </h2>
                            <small>
                                {activeTab === 'formations' ?
                                    'Administration des formations' :
                                    'Administration des produits'
                                }
                            </small>

                            <small id='hbreadcrumb' className='d-flex justify-content-end'>
                                <ol className="breadcrumb custom-breadcrumb">
                                    <li className="breadcrumb-item custom-breadcrumb-item">
                                        <Link to={`/admin`}> Administration</Link>
                                    </li>
                                    <li className="breadcrumb-item custom-breadcrumb-item">
                                        <Link to={`/Liste-formation-produit`}>
                                            {activeTab === 'formations' ? 'Formations' : 'Produits'}
                                        </Link>
                                    </li>
                                </ol>
                            </small>
                        </div>
                    </div>

                    <div className="mt-3">
                        <div className="wrapper wrapper-content animated fadeInRight">

                            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-3">
                                <h5>
                                    {activeTab === 'formations' ?
                                        'Liste des formations' :
                                        'Liste des produits'
                                    }
                                </h5>
                                {renderActionButtons()}
                            </div>

                            {alert.message && (
                                <div className={`alert alert-${alert.type}`}>
                                    {alert.message}
                                </div>
                            )}

                            <div className="row">
                                <div className="col-lg-12">
                                    <div className="ibox">
                                        {/* Onglets personnalisés - Style Bootstrap */}
                                        <div className="mb-0">
                                            <ul className="nav nav-tabs" role="tablist">
                                                <li className="nav-item">
                                                    <button
                                                        className={`nav-link ${activeTab === 'formations' ? 'active' : ''}`}
                                                        onClick={() => {
                                                            setActiveTab('formations');
                                                            setSelectedProduit(null);
                                                        }}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '8px',
                                                            fontWeight: activeTab === 'formations' ? 'bold' : 'normal'
                                                        }}
                                                    >
                                                        <FaChalkboardTeacher />
                                                        Formations
                                                        <span className="badge badge-secondary ml-1">
                                                            {formation.length}
                                                        </span>
                                                    </button>
                                                </li>
                                                <li className="nav-item">
                                                    <button
                                                        className={`nav-link ${activeTab === 'produits' ? 'active' : ''}`}
                                                        onClick={() => {
                                                            setActiveTab('produits');
                                                            setSelectedFormation(null);
                                                        }}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '8px',
                                                            fontWeight: activeTab === 'produits' ? 'bold' : 'normal'
                                                        }}
                                                    >
                                                        <FaBoxes />
                                                        Produits
                                                        <span className="badge badge-secondary ml-1">
                                                            {produits.length}
                                                        </span>
                                                    </button>
                                                </li>
                                            </ul>
                                        </div>
                                        <div className="ibox-content">
                                            {isLoading && activeTab === 'formations' ? (
                                                <div className="text-center p-5">
                                                    <div className="d-flex justify-content-center align-items-center">
                                                        <div className="spiner-example">
                                                            <div className="sk-spinner sk-spinner-wave">
                                                                <div className="sk-rect1 mr-1"></div>
                                                                <div className="sk-rect2 mr-1"></div>
                                                                <div className="sk-rect3 mr-1"></div>
                                                                <div className="sk-rect4 mr-1"></div>
                                                                <div className="sk-rect5"></div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="mt-2">Chargement des formations...</div>
                                                </div>
                                            ) : isLoadingProduit && activeTab === 'produits' ? (
                                                <div className="text-center p-5">
                                                    <div className="d-flex justify-content-center align-items-center">
                                                        <div className="spiner-example">
                                                            <div className="sk-spinner sk-spinner-wave">
                                                                <div className="sk-rect1 mr-1"></div>
                                                                <div className="sk-rect2 mr-1"></div>
                                                                <div className="sk-rect3 mr-1"></div>
                                                                <div className="sk-rect4 mr-1"></div>
                                                                <div className="sk-rect5"></div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="mt-2">Chargement des produits...</div>
                                                </div>
                                            ) : (
                                                renderTable()
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ============================================
                    MODALS POUR LES FORMATIONS
                    ============================================ */}

                    {/* Modal de suppression de formation */}
                    <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                        <Modal.Header>
                            <Modal.Title>Confirmer la suppression</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            Êtes-vous sûr de vouloir supprimer la formation : {selectedFormation && selectedFormation.nom} ?
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                                Annuler
                            </Button>
                            <Button variant="success" onClick={handleDeleteFormation}>
                                Supprimer
                            </Button>
                        </Modal.Footer>
                    </Modal>

                    {/* Modal pour générer le code promo pour formation */}
                    <Modal show={showCodeModal} onHide={() => setShowCodeModal(false)}>
                        <Modal.Header>
                            <Modal.Title>Créer un code promo</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label>Date de fin de la promotion</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={promoEndDate}
                                        onChange={(e) => setPromoEndDate(e.target.value)}
                                        min={new Date().toISOString().split('T')[0]}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Pourcentage de réduction</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={promoPercentage}
                                        onChange={(e) => setPromoPercentage(e.target.value)}
                                        min="1"
                                        max="100"
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Code promo</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={code_promo}
                                        onChange={(e) => setCode_promo(e.target.value)}
                                    />
                                </Form.Group>
                            </Form>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowCodeModal(false)}>
                                Annuler
                            </Button>
                            <Button
                                variant="success"
                                onClick={handleGenererCode}
                                disabled={isLoadingButton}
                            >
                                {isLoadingButton ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                        En cours...
                                    </>
                                ) : 'Générer le code'}
                            </Button>
                        </Modal.Footer>
                    </Modal>

                    {/* Modal de modification de formation */}
                    <Modal show={showEditModal} onHide={handleCloseEditModal} size="lg">
                        <Modal.Header>
                            <Modal.Title>Modifier la formation</Modal.Title>
                        </Modal.Header>
                        <Modal.Body className="p-4">
                            <Form onSubmit={handleEditSubmit}>
                                <Row className="g-3">
                                    {/* Photo */}
                                    <Col md={12} className='mb-4'>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold mb-3">Photo de la formation <span className="text-danger">*</span></Form.Label>
                                            <div style={{ border: '1px solid #dee2e6', borderRadius: '8px', backgroundColor: '#f8f9fa', padding: '16px' }}>
                                                <div style={{ position: 'relative' }}>
                                                    {photoPreview ? (
                                                        <div style={{ position: 'relative' }}>
                                                            <img
                                                                src={photoPreview}
                                                                alt="Preview"
                                                                style={{
                                                                    width: '100%',
                                                                    height: '250px',
                                                                    objectFit: 'cover',
                                                                    display: 'block',
                                                                    borderRadius: '8px'
                                                                }}
                                                            />
                                                            <div style={{ position: 'absolute', top: '8px', right: '8px' }}>
                                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                                    <label
                                                                        htmlFor="edit-photo-upload"
                                                                        style={{
                                                                            width: '40px',
                                                                            height: '40px',
                                                                            borderRadius: '50%',
                                                                            backgroundColor: 'white',
                                                                            border: '1px solid #dee2e6',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            cursor: 'pointer',
                                                                            fontSize: '14px'
                                                                        }}
                                                                        title="Changer la photo"
                                                                    >
                                                                        <FaCamera style={{ color: '#007bff' }} />
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div style={{
                                                            textAlign: 'center',
                                                            padding: '40px',
                                                            border: '1px solid #dee2e6',
                                                            borderRadius: '8px',
                                                            backgroundColor: 'white'
                                                        }}>
                                                            <div style={{ marginBottom: '16px' }}>
                                                                <FaImage size={50} style={{ color: '#6c757d' }} />
                                                            </div>
                                                            <label
                                                                htmlFor="edit-photo-upload"
                                                                style={{
                                                                    backgroundColor: '#007bff',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    padding: '8px 16px',
                                                                    borderRadius: '4px',
                                                                    cursor: 'pointer',
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: '8px',
                                                                    fontSize: '14px'
                                                                }}
                                                            >
                                                                <FaCamera />
                                                                Ajouter une photo
                                                            </label>
                                                        </div>
                                                    )}

                                                    <input
                                                        id="edit-photo-upload"
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handlePhotoChange}
                                                        style={{ display: 'none' }}
                                                    />
                                                </div>
                                            </div>
                                        </Form.Group>
                                    </Col>

                                    {/* Formulaire de formation */}
                                    <Col md={6} className='mb-2'>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Nom <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                required
                                                value={editFormationData.nom}
                                                onChange={(e) => setEditFormationData({ ...editFormationData, nom: e.target.value })}
                                                placeholder="Nom de la formation"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6} className='mb-2'>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Référence <span className="text-danger"></span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                required
                                                value={editFormationData.reference}
                                                onChange={(e) => setEditFormationData({ ...editFormationData, reference: e.target.value })}
                                                placeholder="Référence de la formation"
                                                readOnly
                                            />
                                            {/* <small>Si vous ne renseignez rien, cette référence sera auto-générée.</small> */}
                                        </Form.Group>
                                    </Col>

                                    <Col md={6} className='mb-2'>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Tarif public (FCFA HT) <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="number"
                                                required
                                                value={editFormationData.prix}
                                                onChange={(e) => setEditFormationData({ ...editFormationData, prix: e.target.value })}
                                                placeholder="Prix en FCFA HT"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={6} className='mb-2'>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Tarif particulier (FCFA HT) <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="number"
                                                required
                                                value={editFormationData.prix_particulier}
                                                onChange={(e) => setEditFormationData({ ...editFormationData, prix_particulier: e.target.value })}
                                                placeholder="Prix en FCFA HT"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={6} className='mb-2'>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Code formation <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                required
                                                value={editFormationData.code}
                                                onChange={(e) => setEditFormationData({ ...editFormationData, code: e.target.value })}
                                                placeholder="Code formation"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={6} className='mb-2'>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Lieu <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                required
                                                value={editFormationData.lieu}
                                                onChange={(e) => setEditFormationData({ ...editFormationData, lieu: e.target.value })}
                                                placeholder="Lieu de la formation"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={6} className='mb-2'>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Option <span className="text-danger">*</span></Form.Label>
                                            <Form.Select
                                                className='custom-select'
                                                value={editFormationData.option}
                                                onChange={(e) => setEditFormationData({ ...editFormationData, option: e.target.value })}
                                                required
                                            >
                                                <option value="en_ligne">Virtuel</option>
                                                <option value="presentiel">Présentiel</option>
                                                <option value="deux">Les deux</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6} className='mb-2'>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Nombre de participants <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                value={editFormationData.nb_participants}
                                                onChange={(e) => setEditFormationData({ ...editFormationData, nb_participants: e.target.value })}
                                                placeholder="Nombre de participants"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={6} className='mb-2'>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Période de coaching <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                value={editFormationData.periode_coaching}
                                                onChange={(e) => setEditFormationData({ ...editFormationData, periode_coaching: e.target.value })}
                                                placeholder="Période de coaching"
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* Gestion des dates multiples */}
                                    <Col md={12} className='mb-2 mt-2'>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                Dates de la formation
                                                <span className="badge badge-secondary ml-2">
                                                    {selectedDates.length} date{selectedDates.length !== 1 ? 's' : ''}
                                                </span>
                                            </Form.Label>

                                            <div className="card border-0 bg-light">
                                                <div className="card-body p-3">
                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                        <Form.Control
                                                            type="date"
                                                            size="sm"
                                                            style={{ width: '200px' }}
                                                            min={getMinDate()}
                                                            onChange={(e) => {
                                                                if (e.target.value) {
                                                                    handleAddDate(e.target.value);
                                                                    e.target.value = '';
                                                                }
                                                            }}
                                                        />
                                                    </div>

                                                    <Alert variant="info" className="p-2 small mb-3">
                                                        <FaExclamationTriangle className="mr-2" />
                                                        <strong>Instructions :</strong> Ajoutez les dates dans n'importe quel ordre, elles seront automatiquement triées.
                                                    </Alert>

                                                    {selectedDates.length > 0 ? (
                                                        <div className="selected-dates-list">
                                                            <div className="d-flex flex-column gap-2">
                                                                {selectedDates.map((date, index) => (
                                                                    <div key={index} className="d-flex align-items-center justify-content-between p-2 border rounded bg-white">
                                                                        <div className="d-flex align-items-center">
                                                                            <FaCalendarAlt className="mr-2 text-primary" />
                                                                            <div>
                                                                                <strong>{formatDateDisplay(date)}</strong>
                                                                                {index === 0 && <span className="badge bg-success ml-2">Première date</span>}
                                                                                {index === selectedDates.length - 1 && <span className="badge bg-info ml-2">Dernière date</span>}
                                                                            </div>
                                                                        </div>
                                                                        <Button
                                                                            variant="outline-danger"
                                                                            size="sm"
                                                                            onClick={() => handleRemoveDate(date)}
                                                                            className="d-flex align-items-center"
                                                                        >
                                                                            <FaTimes />
                                                                        </Button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <Alert variant="warning" className="mb-0 d-flex align-items-center">
                                                            <FaCalendarAlt className="me-2" />
                                                            {selectedFormation?.periode ?
                                                                "Format de dates non supporté. Veuillez ajouter de nouvelles dates." :
                                                                "Aucune date sélectionnée. Ajoutez des dates pour votre formation."
                                                            }
                                                        </Alert>
                                                    )}
                                                </div>
                                            </div>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Form>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseEditModal}>
                                Annuler
                            </Button>
                            <Button variant="success" onClick={handleEditSubmit}>
                                Enregistrer les modifications
                            </Button>
                        </Modal.Footer>
                    </Modal>

                    {/* ============================================
                    MODALS POUR LES PRODUITS
                    ============================================ */}

                    {/* Modal de suppression de produit */}
                    <Modal show={showDeleteModalProduit} onHide={() => setShowDeleteModalProduit(false)}>
                        <Modal.Header>
                            <Modal.Title>Confirmer la suppression</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            Êtes-vous sûr de vouloir supprimer le produit : {selectedProduit && selectedProduit.nom} ?
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowDeleteModalProduit(false)}>
                                Annuler
                            </Button>
                            <Button variant="success" onClick={handleDeleteProduit}>
                                Supprimer
                            </Button>
                        </Modal.Footer>
                    </Modal>

                    {/* Modal d'ajout de produit */}
                    <Modal show={showAjouterModal} onHide={handleCloseAjouterModal} size="lg">
                        <Modal.Header>
                            <Modal.Title>Nouveau Produit</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form onSubmit={handleAjouterProduit} encType="multipart/form-data">
                                <Form.Group className="mb-3">
                                    <Form.Label>Nom*</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Entrez le nom"
                                        required
                                        name="nom"
                                        value={formDataProduit.nom}
                                        onChange={handleInputChangeProduit}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Description*</Form.Label>
                                    <ReactQuill
                                        theme="snow"
                                        value={formDataProduit.description}
                                        onChange={handleDescriptionChangeProduit}
                                        modules={modules}
                                        style={{ height: '200px', marginBottom: '50px' }}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Prix*</Form.Label>
                                    <Form.Control
                                        type="number"
                                        placeholder="Entrez le Prix"
                                        required
                                        name="prix"
                                        value={formDataProduit.prix}
                                        onChange={handleInputChangeProduit}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Période*</Form.Label>
                                    <Form.Select
                                        className='custom-select'
                                        name="periode"
                                        required
                                        value={formDataProduit.periode}
                                        onChange={handleInputChangeProduit}
                                    >
                                        <option value="">Sélectionner une période</option>
                                        {[...Array(12)].map((_, index) => {
                                            const mois = index + 1;
                                            const heures = mois * 730;
                                            return (
                                                <option key={mois} value={heures}>
                                                    {mois} {mois === 1 ? "mois" : "mois"} ({heures} heures)
                                                </option>
                                            );
                                        })}
                                    </Form.Select>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Photo*</Form.Label>
                                    <Form.Control
                                        type="file"
                                        required
                                        name="photo"
                                        accept="image/*"
                                        onChange={handleFileChangeProduit}
                                    />
                                </Form.Group>
                                <div className="text-center mt-4">
                                    <Button variant="secondary" onClick={handleCloseAjouterModal} className="mr-2">
                                        Annuler
                                    </Button>
                                    <Button type="submit" variant="success">
                                        Enregistrer
                                    </Button>
                                </div>
                            </Form>
                        </Modal.Body>
                    </Modal>

                    {/* Modal de modification de produit */}
                    <Modal show={showModifierModal} onHide={handleCloseModifierModal} size="lg">
                        <Modal.Header>
                            <Modal.Title>Modifier le Produit</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form onSubmit={handleModifierProduit} encType="multipart/form-data">
                                <Form.Group className="mb-3">
                                    <Form.Label>Nom*</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Entrez le nom"
                                        required
                                        name="nom"
                                        value={formDataProduit.nom}
                                        onChange={handleInputChangeProduit}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Description*</Form.Label>
                                    <ReactQuill
                                        theme="snow"
                                        value={formDataProduit.description}
                                        onChange={handleDescriptionChangeProduit}
                                        modules={modules}
                                        style={{ height: '200px', marginBottom: '50px' }}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Prix*</Form.Label>
                                    <Form.Control
                                        type="number"
                                        placeholder="Entrez le Prix"
                                        required
                                        name="prix"
                                        value={formDataProduit.prix}
                                        onChange={handleInputChangeProduit}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Période*</Form.Label>
                                    <Form.Select
                                        className='custom-select'
                                        name="periode"
                                        required
                                        value={formDataProduit.periode}
                                        onChange={handleInputChangeProduit}
                                    >
                                        <option value="">Sélectionner une période</option>
                                        {[...Array(12)].map((_, index) => {
                                            const mois = index + 1;
                                            const heures = mois * 730;
                                            return (
                                                <option key={mois} value={heures}>
                                                    {mois} {mois === 1 ? "mois" : "mois"} ({heures} heures)
                                                </option>
                                            );
                                        })}
                                    </Form.Select>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Photo</Form.Label>
                                    <Form.Control
                                        type="file"
                                        name="photo"
                                        accept="image/*"
                                        onChange={handleFileChangeProduit}
                                    />
                                    <Form.Text className="text-muted">
                                        Laisser vide pour conserver l'image actuelle
                                    </Form.Text>
                                </Form.Group>
                                <div className="text-center mt-4">
                                    <Button variant="secondary" onClick={handleCloseModifierModal} className="mr-2">
                                        Annuler
                                    </Button>
                                    <Button type="submit" variant="success">
                                        Modifier
                                    </Button>
                                </div>
                            </Form>
                        </Modal.Body>
                    </Modal>
                </div>
            </div>
        </div>
    );
};

export default Liste_formation;